import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/* ============================================================
   COURSE CATEGORIES  (table: course_categories)
   columns: category_id (uuid, pk), name (varchar), created_at
   ============================================================ */

export const courseCategoryService = {
  async getAll() {
    const { data, error } = await supabase
      .from('course_categories')
      .select('*')
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data;
  },

  async add(name) {
    const { data, error } = await supabase
      .from('course_categories')
      .insert([{ name }])
      .select()
      .single();

    if (error) {
      // Postgres unique-constraint violation -> friendlier message
      if (error.code === '23505') throw new Error('This category already exists.');
      throw error;
    }
    return data;
  },

  async remove(categoryId) {
    const { error } = await supabase
      .from('course_categories')
      .delete()
      .eq('category_id', categoryId);

    if (error) throw error;
    return true;
  }
};

/* ============================================================
   COURSES  (table: course)
   columns: course_id (uuid pk), course_code, name, type,
            category_id (fk -> course_categories), delivered_by,
            delivery_date, price, currency, status, source,
            partner_id, created_at, updated_at
   ============================================================ */

export const courseService = {
  async getAll() {
    // join course_categories so we get the category NAME along with category_id
    // without a second round trip for every row
    const { data, error } = await supabase
      .from('course')
      .select(`*, course_categories ( name )`)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data;
  },

  async add(payload) {
    // payload must already have category_id (not category name) + course_code
    const { data, error } = await supabase
      .from('course')
      .insert([payload])
      .select(`*, course_categories ( name )`)
      .single();

    if (error) throw error;
    return data;
  },

  async update(courseId, payload) {
    const { data, error } = await supabase
      .from('course')
      .update(payload)
      .eq('course_id', courseId)
      .select(`*, course_categories ( name )`)
      .single();

    if (error) throw error;
    return data;
  },

  async remove(courseId) {
    const { error } = await supabase.from('course').delete().eq('course_id', courseId);
    if (error) throw error;
    return true;
  }
};

/* ============================================================
   PARTNER INDUSTRIES  (table: primary_industries)
   columns: industry_id (uuid pk), name (varchar), created_at
   referenced by partners.primary_industry_id
   ============================================================ */

export const industryService = {
  async getAll() {
    const { data, error } = await supabase
      .from('primary_industries')
      .select('*')
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data;
  },

  async add(name) {
    const { data, error } = await supabase
      .from('primary_industries')
      .insert([{ name }])
      .select()
      .single();

    if (error) {
      if (error.code === '23505') throw new Error('This industry already exists.');
      throw error;
    }
    return data;
  },

  async remove(industryId) {
    const { error } = await supabase
      .from('primary_industries')
      .delete()
      .eq('industry_id', industryId);

    if (error) throw error;
    return true;
  }
};


/* ============================================================
   PARTNERS  (table: partners)
   columns: partner_id (uuid pk), partner_code, first_name, last_name,
            email, phone, company_name,
            primary_industry_id (fk -> primary_industries),
            partner_type, password, agreement_file_url, status,
            created_at, updated_at

   NOTE: `contact_person` has been replaced by `first_name` + `last_name`.
   `password` is a plain-text column used for the partner's own login
   (see PartnerLogin.jsx) — partners are NOT created via Supabase Auth,
   they're created directly in this table by Wiiz Admin. Agreement upload
   is optional and no longer gates partner status.

   partner_category_permissions (table): links a partner to the
   course_categories their partner_type is eligible to create content in
   ============================================================ */

export const partnerService = {
  async getAll() {
    // pulls industry name + eligible category names in one query via joins
    const { data, error } = await supabase
      .from('partners')
      .select(`
        *,
        primary_industries ( name ),
        partner_category_permissions ( category_id, course_categories ( name ) )
      `)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data;
  },

  // payload: { partner_code, first_name, last_name, email, phone, company_name,
  //            primary_industry_id, partner_type, password, status }
  async add(payload) {
    const { data, error } = await supabase
      .from('partners')
      .insert([payload])
      .select(`*, primary_industries ( name )`)
      .single();

    if (error) throw error;
    return data;
  },

  // updates an existing partner's editable fields. payload can optionally
  // include `password` — only pass it when the admin actually wants to
  // change it (Partners.jsx omits it otherwise).
  async update(partnerId, payload) {
    const { data, error } = await supabase
      .from('partners')
      .update(payload)
      .eq('partner_id', partnerId)
      .select(`*, primary_industries ( name )`)
      .single();

    if (error) throw error;
    return data;
  },

  // deletes a partner row. Related partner_category_permissions rows should
  // have ON DELETE CASCADE on the FK — if not, delete those first here.
  async remove(partnerId) {
    const { error } = await supabase
      .from('partners')
      .delete()
      .eq('partner_id', partnerId);

    if (error) throw error;
    return true;
  },

  // called right after add() with the category_ids the chosen partner_type unlocks
  async setCategoryPermissions(partnerId, categoryIds) {
    if (!categoryIds.length) return [];
    const rows = categoryIds.map(category_id => ({ partner_id: partnerId, category_id }));

    const { data, error } = await supabase
      .from('partner_category_permissions')
      .insert(rows)
      .select();

    if (error) throw error;
    return data;
  },

  // uploads the signed PDF to a `partner-agreements` storage bucket, then
  // stores the file url on the partner row. Agreement is OPTIONAL now —
  // this no longer flips partner status, since partners are created
  // 'active' up front and log in with email + password directly.
  async uploadAgreement(partnerId, file) {
    const path = `${partnerId}/${file.name}`;
    const { error: uploadError } = await supabase
      .storage
      .from('partner-agreements')
      .upload(path, file, { upsert: true });

    if (uploadError) throw uploadError;

    const { data: urlData } = supabase.storage.from('partner-agreements').getPublicUrl(path);

    const { data, error } = await supabase
      .from('partners')
      .update({ agreement_file_url: urlData.publicUrl })
      .eq('partner_id', partnerId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};

/* ============================================================
   ALLOCATIONS  (table: allocations)
   columns: allocation_id (uuid pk), partner_id (fk -> partners),
            course_id (fk -> course), seats (int), seats_used (int),
            valid_from (date), valid_until (date), status (varchar),
            created_at, updated_at

   Grants a partner the right to register people against a course, with
   seat and validity limits. Joins in partner + course display fields so
   the Allocation page doesn't need a second round trip per row.
   ============================================================ */

export const allocationService = {
  async getAll() {
    const { data, error } = await supabase
      .from('allocations')
      .select(`
        *,
        partners ( partner_id, first_name, last_name, company_name ),
        course ( course_id, name, course_code, type, delivered_by, price, currency, status )
      `)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data;
  },

  // used by the partner-side "My Content" view — only this partner's
  // active allocations, already joined with the full course details.
  async getForPartner(partnerId) {
    const { data, error } = await supabase
      .from('allocations')
      .select(`
        *,
        course ( course_id, name, course_code, type, delivered_by, price, currency, status )
      `)
      .eq('partner_id', partnerId)
      .eq('status', 'active')
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data;
  },

  // payload: { partner_id, course_id, seats, seats_used, valid_from, valid_until, status }
  async add(payload) {
    const { data, error } = await supabase
      .from('allocations')
      .insert([payload])
      .select(`
        *,
        partners ( partner_id, first_name, last_name, company_name ),
        course ( course_id, name, course_code, type, delivered_by, price, currency, status )
      `)
      .single();

    if (error) throw error;
    return data;
  },

  async remove(allocationId) {
    const { error } = await supabase
      .from('allocations')
      .delete()
      .eq('allocation_id', allocationId);

    if (error) throw error;
    return true;
  },

  // bumps seats_used up (registration) or down (removing a registration).
  // Reads the current value first since Supabase doesn't do atomic
  // increments from the client without an RPC function.
  async adjustSeatsUsed(allocationId, delta) {
    const { data: current, error: fetchError } = await supabase
      .from('allocations')
      .select('seats_used')
      .eq('allocation_id', allocationId)
      .single();

    if (fetchError) throw fetchError;

    const nextValue = Math.max(0, (current.seats_used || 0) + delta);

    const { data, error } = await supabase
      .from('allocations')
      .update({ seats_used: nextValue })
      .eq('allocation_id', allocationId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};

/* ============================================================
   STUDENTS  (table: students)
   columns: student_id (uuid pk), first_name, last_name, email,
            mobile, organisation, created_at, updated_at

   One row per PERSON, not per registration — the same person can be
   registered against several courses (see `requests` below), so we
   look them up by email first and reuse the row instead of creating
   duplicates.
   ============================================================ */

// Short, readable student ID — e.g. "STU-2026-K3F9QZ". The actual DB
// column is `character varying(30)`, NOT uuid (despite the diagram),
// so a full crypto.randomUUID() (36 chars) overflows it with
// "value too long for type character varying(30)". This format is
// well under 30 chars and still effectively unique per registration.
function generateStudentId() {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).slice(2, 8).toUpperCase(); // 6 chars
  return `STU-${year}-${random}`; // 14 chars total
}

export const studentService = {
  async getAll() {
    const { data, error } = await supabase
      .from('students')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // case-insensitive lookup so "John@x.com" and "john@x.com" match the
  // same person. Returns null if nobody has registered with this email yet.
  async findByEmail(email) {
    const { data, error } = await supabase
      .from('students')
      .select('*')
      .ilike('email', email.trim())
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  // payload: { first_name, last_name, email, mobile, organisation }
  // The `students.student_id` column is NOT NULL with no DB-side
  // default, so we generate a short id here and send it explicitly.
  // Retries once with a fresh id on the rare chance of a collision
  // (23505 = unique_violation) instead of failing the registration.
  async add(payload) {
    const attempt = async () => {
      const row = { student_id: generateStudentId(), ...payload };
      return supabase.from('students').insert([row]).select().single();
    };

    let { data, error } = await attempt();

    if (error && error.code === '23505') {
      ({ data, error } = await attempt());
    }

    if (error) throw error;
    return data;
  },

  async update(studentId, payload) {
    const { data, error } = await supabase
      .from('students')
      .update(payload)
      .eq('student_id', studentId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};

/* ============================================================
   REQUESTS  (table: requests)
   columns: enrollment_id (character varying(30) pk), student_id (fk -> students),
            course_id (fk -> course), allocation_id (fk -> allocations),
            partner_id (fk -> partners), payment_status, status,
            created_at, updated_at

   One row per REGISTRATION — a student registering against a course
   under a specific partner allocation. The same student can have many
   rows here: several for the same course (re-registration) or one
   per different course.

   `status` doubles as the registration state AND drives the
   certification-request flow. The DB check constraint
   (requests_status_check) only allows:
     'registered' | 'pending' | 'completed' | 'cancelled'
   Flow: registered (on signup) -> pending (partner clicks 🎓 Request
   Certification) -> completed (admin approves, certificate issued) /
   cancelled (admin rejects). Do NOT send any other status string —
   e.g. 'certification_requested', 'approved', 'rejected' — the DB
   will reject the write.
   ============================================================ */

// Short, readable enrollment ID — e.g. "ENR-2026-K3F9QZ".
// requests.enrollment_id is `character varying(30)`, NOT uuid (despite
// the diagram) — it's NOT NULL with no DB-side default, and a full
// crypto.randomUUID() (36 chars) overflows it with "value too long for
// type character varying(30)". Same fix as students.student_id above.
function generateEnrollmentId() {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).slice(2, 8).toUpperCase(); // 6 chars
  return `ENR-${year}-${random}`; // 14 chars total
}

export const requestService = {
  // all registrations made by one partner, joined with student +
  // course details so the UI doesn't need extra round trips.
  async getForPartner(partnerId) {
    const { data, error } = await supabase
      .from('requests')
      .select(`
        *,
        students ( student_id, first_name, last_name, email, mobile, organisation ),
        course ( course_id, name, course_code, type )
      `)
      .eq('partner_id', partnerId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // admin-side: every request across ALL partners, joined with
  // student, course, and partner display fields. Used by the admin
  // Requests page to review certification requests.
  async getAll() {
    const { data, error } = await supabase
      .from('requests')
      .select(`
        *,
        students ( student_id, first_name, last_name, email, mobile, organisation ),
        course ( course_id, name, course_code, type, price, currency ),
        partners ( partner_id, first_name, last_name, company_name )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // payload: { student_id, course_id, allocation_id, partner_id, payment_status, status }
  // Generates enrollment_id here (same pattern as studentService.add) and
  // retries once with a fresh id on the rare chance of a collision.
  async add(payload) {
    const attempt = async () => {
      const row = { enrollment_id: generateEnrollmentId(), ...payload };
      return supabase
        .from('requests')
        .insert([row])
        .select(`
          *,
          students ( student_id, first_name, last_name, email, mobile, organisation ),
          course ( course_id, name, course_code, type )
        `)
        .single();
    };

    let { data, error } = await attempt();

    if (error && error.code === '23505') {
      ({ data, error } = await attempt());
    }

    if (error) throw error;
    return data;
  },

  // status must be one of: 'registered' | 'pending' | 'completed' | 'cancelled'
  async updateStatus(enrollmentId, status) {
    const { data, error } = await supabase
      .from('requests')
      .update({ status })
      .eq('enrollment_id', enrollmentId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async remove(enrollmentId) {
    const { error } = await supabase
      .from('requests')
      .delete()
      .eq('enrollment_id', enrollmentId);

    if (error) throw error;
    return true;
  }
};

/* ============================================================
   CERTIFICATES  (table: certificates)
   columns: id (uuid pk), certificate_id (varchar), student_id (fk),
            course_id (fk), partner_id (fk), certificate_type,
            pdf_url, issue_date, created_at, updated_at

   One row per issued certificate, keyed by (student_id, course_id) —
   NOT by enrollment_id — since a certificate belongs to a person
   having completed a course, independent of which specific
   registration row triggered it.

   ASSUMPTION: storage bucket is named 'certificates'. If your bucket
   has a different name, change the .storage.from('certificates')
   calls below to match.
   ============================================================ */

// Short, readable certificate ID — e.g. "CERT-2026-K3F9QZ". Same
// short-varchar pattern as student_id / enrollment_id above.
function generateCertificateId() {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `CERT-${year}-${random}`;
}

export const certificateService = {
  async getAll() {
    const { data, error } = await supabase
      .from('certificates')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // uploads the generated PDF blob to the certificates bucket and
  // returns its public URL.
  async uploadPdf(blob, path) {
    const { error: uploadError } = await supabase
      .storage
      .from('certificates')
      .upload(path, blob, { upsert: true, contentType: 'application/pdf' });

    if (uploadError) throw uploadError;

    const { data } = supabase.storage.from('certificates').getPublicUrl(path);
    return data.publicUrl;
  },

  // payload: { student_id, course_id, partner_id, certificate_type, pdf_url, issue_date }
  // certificate_id is generated here (short varchar, same pattern as
  // student_id / enrollment_id) and retried once on the rare chance
  // of a collision.
  async add(payload) {
    const attempt = async () => {
      const row = { certificate_id: generateCertificateId(), ...payload };
      return supabase.from('certificates').insert([row]).select().single();
    };

    let { data, error } = await attempt();

    if (error && error.code === '23505') {
      ({ data, error } = await attempt());
    }

    if (error) throw error;
    return data;
  }
};

